// eslint configured: rule not present in CRA build
import { useEffect, useMemo, useState } from "react";
import { apiGet, PLATFORMS, STAGES } from "@/lib/api";
import { Search, Plus, Lightbulb } from "lucide-react";
import IdeaModal from "@/components/IdeaModal";
import EmptyState from "@/components/EmptyState";

const SEAL = "var(--seal)";
const INK = "var(--ink)";
const INK_55 = "var(--ink-55)";
const INK_72 = "var(--ink-72)";

export default function Vault() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [platform, setPlatform] = useState("");
  const [stage, setStage] = useState("");
  const [tag, setTag] = useState("");
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);

  const load = async () => setItems(await apiGet("/ideas"));
  useEffect(() => { load(); }, []);

  const allTags = useMemo(() => {
    const s = new Set();
    items.forEach((i) => (i.tags || []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [items]);

  const filtered = items.filter((i) => {
    if (platform && i.platform !== platform) return false;
    if (stage && i.stage !== stage) return false;
    if (tag && !(i.tags || []).includes(tag)) return false;
    if (q) {
      const hay = `${i.title} ${i.description || ""} ${(i.tags || []).join(" ")}`.toLowerCase();
      if (!hay.includes(q.toLowerCase())) return false;
    }
    return true;
  });

  return (
    <div className="max-w-6xl mx-auto">
      <div className="flex items-end justify-between flex-wrap gap-3 mb-8">
        <div>
          <div className="eyebrow"><span className="seal-dot mr-2 align-middle" /> i. The Vault</div>
          <h1 className="font-heading mt-4 text-4xl md:text-5xl" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
            Every <span className="font-display-italic">spark</span>, kept.
          </h1>
        </div>
        <button className="btn-primary" onClick={() => setShowNew(true)} data-testid="vault-new-btn">
          <Plus size={14} /> New idea
        </button>
      </div>

      <div className="space-y-3 mb-8">
        <div className="relative">
          <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2" style={{ color: "var(--ink-38)" }} />
          <input
            data-testid="vault-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search ideas"
            className="input pl-10"
          />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select value={platform} onChange={(e) => setPlatform(e.target.value)} className="input w-auto text-xs" data-testid="vault-filter-platform">
            <option value="">All platforms</option>
            {PLATFORMS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
          <select value={stage} onChange={(e) => setStage(e.target.value)} className="input w-auto text-xs" data-testid="vault-filter-stage">
            <option value="">All stages</option>
            {STAGES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          {allTags.length > 0 && (
            <select value={tag} onChange={(e) => setTag(e.target.value)} className="input w-auto text-xs" data-testid="vault-filter-tag">
              <option value="">All tags</option>
              {allTags.map((t) => <option key={t} value={t}>#{t}</option>)}
            </select>
          )}
        </div>
      </div>

      {filtered.length === 0 ? (
        items.length === 0 ? (
          <EmptyState
            testid="vault-empty"
            icon={Lightbulb}
            eyebrow="The Vault"
            title="Where every spark"
            titleItalic="lives."
            body="Capture an idea before it slips. Tag it, search it later, never lose another flash of inspiration."
            primaryLabel="Capture first idea"
            onPrimary={() => setShowNew(true)}
          />
        ) : (
          <div className="card text-center py-16">
            <div className="font-heading text-2xl mb-2" style={{ fontVariationSettings: "'opsz' 96, 'wght' 450" }}>
              No ideas match
            </div>
            <div className="text-sm" style={{ color: INK_55 }}>Try clearing filters or capture a new idea.</div>
          </div>
        )
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-grid">
          {filtered.map((i) => {
            const platformObj = PLATFORMS.find((p) => p.id === i.platform);
            const stageObj = STAGES.find((s) => s.id === i.stage);
            return (
              <button
                key={i.id}
                onClick={() => setActive(i)}
                data-testid={`vault-card-${i.id}`}
                className="card text-left hover:-translate-y-0.5 transition-transform"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="tag">{platformObj?.short}</span>
                  <span className="tag-brass">{stageObj?.label}</span>
                </div>
                <div className="leading-snug" style={{ color: INK, fontFamily: "'Fraunces', serif", fontVariationSettings: "'opsz' 32, 'wght' 450", fontSize: "1.05rem" }}>
                  {i.title}
                </div>
                {i.description && (
                  <div className="text-xs mt-2 line-clamp-3" style={{ color: INK_55 }}>{i.description}</div>
                )}
                {(i.tags || []).length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-3">
                    {i.tags.map((t) => <span key={t} className="tag" style={{ background: "transparent", color: INK_55 }}>#{t}</span>)}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {active && <IdeaModal idea={active} onClose={() => setActive(null)} onSaved={() => { setActive(null); load(); }} onDeleted={() => { setActive(null); load(); }} />}
      {showNew && <IdeaModal onClose={() => setShowNew(false)} onSaved={() => { setShowNew(false); load(); }} />}
    </div>
  );
}
